// src/components/Analytics/LeaveAnalytics.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { db } from "../../firebase/config";
import { collection, getDoc, getDocs, doc } from "firebase/firestore";
import { Pie, Bar } from "react-chartjs-2";
import { DateTime } from "luxon";

import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import "../../styles/Analytics.css";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

// ─────────────────────────── Date range helpers
const formatMDY = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${m}/${d}/${y}`;
};

function buildRangeFromPreset(preset, tz) {
  const end = DateTime.now()
    .setZone(tz)
    .startOf("day");
  let start = end;

  switch (preset) {
    case "LAST_7":
      start = end.minus({ days: 6 });
      break;
    case "LAST_30":
      start = end.minus({ days: 29 });
      break;
    case "LAST_90":
      start = end.minus({ days: 89 });
      break;
    case "MTD":
      start = end.startOf("month");
      break;
    case "YTD":
      start = end.startOf("year");
      break;
    default:
      start = end.minus({ days: 29 });
  }

  return { startISO: start.toISODate(), endISO: end.toISODate() };
}

function inRangeByStartDate(req, startISO, endISO, tz) {
  if (!req?.startDate) return false;

  const d = DateTime.fromISO(req.startDate, { zone: tz }).startOf("day");
  if (!d.isValid) return false;

  const s = DateTime.fromISO(startISO, { zone: tz }).startOf("day");
  const e = DateTime.fromISO(endISO, { zone: tz }).startOf("day");
  if (!s.isValid || !e.isValid) return false;

  const ms = d.toMillis();
  return ms >= s.toMillis() && ms <= e.toMillis();
}

export default function LeaveAnalytics({ companyId, employeeId = null }) {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [companyTimeZone, setCompanyTimeZone] = useState("UTC");
  const [employeesMap, setEmployeesMap] = useState({}); // id -> { name, department }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ✅ Date range state
  const [rangePreset, setRangePreset] = useState("LAST_30");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");

  const isDrilldown = !!employeeId;

  /* ───────────────────────────── Guard ───────────────────────────── */
  useEffect(() => {
    if (!companyId) {
      setError("Company not loaded.");
      setLoading(false);
    } else {
      setError("");
    }
  }, [companyId]);

  /* ───────────────────────────── Company TZ ───────────────────────────── */
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const companyDoc = await getDoc(doc(db, "companies", companyId));
        setCompanyTimeZone(companyDoc.data()?.timeZone || "UTC");
      } catch (e) {
        console.error(e);
        setCompanyTimeZone("UTC");
      }
    })();
  }, [companyId]);

  useEffect(() => {
    if (!companyTimeZone) return;

    if (rangePreset === "CUSTOM") {
      const todayISO = DateTime.now()
        .setZone(companyTimeZone)
        .toISODate();

      // only fill blanks, don’t overwrite user input
      if (!rangeStart) setRangeStart(todayISO);
      if (!rangeEnd) setRangeEnd(todayISO);
      return;
    }

    const { startISO, endISO } = buildRangeFromPreset(
      rangePreset,
      companyTimeZone
    );

    // only update if different (prevents re-render loops)
    if (rangeStart !== startISO) setRangeStart(startISO);
    if (rangeEnd !== endISO) setRangeEnd(endISO);
  }, [rangePreset, companyTimeZone, rangeStart, rangeEnd]); // intentionally not depending on rangeStart/rangeEnd

  /* ✅ Guard: keep start <= end */
  useEffect(() => {
    if (!rangeStart || !rangeEnd) return;
    const s = DateTime.fromISO(rangeStart, { zone: companyTimeZone });
    const e = DateTime.fromISO(rangeEnd, { zone: companyTimeZone });
    if (s.isValid && e.isValid && s > e) setRangeEnd(rangeStart);
  }, [rangeStart, rangeEnd, companyTimeZone]);

  /* ───────────────────────────── Employees map (for dept + names) ───────────────────────────── */
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        const snap = await getDocs(
          collection(db, `companies/${companyId}/employees`)
        );
        const map = {};
        snap.forEach((d) => {
          const data = d.data() || {};
          map[d.id] = {
            name:
              data.name ||
              data.fullName ||
              data.displayName ||
              `${data.firstName || ""} ${data.lastName || ""}`.trim() ||
              "Unknown",
            department:
              data.department ||
              data.group ||
              data.departmentName ||
              "Unknown / Unset",
          };
        });
        setEmployeesMap(map);
      } catch (e) {
        console.error("Failed to fetch employees for LeaveAnalytics:", e);
        setEmployeesMap({});
      }
    })();
  }, [companyId]);

  /* ───────────────────────────── Fetch leave history ───────────────────────────── */
  useEffect(() => {
    if (!companyId) return;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const snapshot = await getDocs(
          collection(db, `companies/${companyId}/leave_history`)
        );
        const leaves = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLeaveRequests(leaves);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch leave data.");
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  /* ───────────────────────────── Helpers ───────────────────────────── */

  // SAME logic as EmployerLeave (Full Day / Half Day + custom times)
  const calcRequestedHours = useCallback((leave) => {
    if (!leave?.startDate || !leave?.endDate) return 0;

    const start = DateTime.fromISO(leave.startDate);
    const end = DateTime.fromISO(leave.endDate);

    if (!start.isValid || !end.isValid) return 0;

    // count every calendar day between start and end (inclusive)
    let days = 0;
    for (
      let d = start;
      d.toMillis() <= end.toMillis();
      d = d.plus({ days: 1 })
    ) {
      days++;
    }

    let hoursPerDay = 0;

    if (leave.timeOfDay === "Full Day") {
      hoursPerDay = 8;
    } else if (leave.timeOfDay === "Half Day") {
      const customStart = leave.customStart;
      const customEnd = leave.customEnd;

      if (customStart && customEnd) {
        const baseDate = "2000-01-01";
        const startDT = DateTime.fromISO(`${baseDate}T${customStart}`);
        const endDT = DateTime.fromISO(`${baseDate}T${customEnd}`);
        const diff = endDT.diff(startDT, "hours").hours;
        hoursPerDay = diff > 0 ? diff : 0;
      } else {
        hoursPerDay = 4;
      }
    } else {
      // legacy values like "Half Day - AM/PM"
      hoursPerDay = 4;
    }

    if (days === 0 || hoursPerDay === 0) return 0;
    return days * hoursPerDay;
  }, []);

  /* ───────────────────────────── Scoped (employee + date range) ───────────────────────────── */
  const employeeScoped = useMemo(() => {
    if (!employeeId) return leaveRequests;

    return leaveRequests.filter((req) => {
      const eid = req.employeeId || req.userId || req.uid || null;
      return (eid || "") === employeeId;
    });
  }, [leaveRequests, employeeId]);

  const scopedLeaveRequests = useMemo(() => {
    if (!rangeStart || !rangeEnd) return employeeScoped;

    return employeeScoped.filter((req) =>
      inRangeByStartDate(req, rangeStart, rangeEnd, companyTimeZone)
    );
  }, [employeeScoped, rangeStart, rangeEnd, companyTimeZone]);

  // convenience subset: approved leaves
  const approvedLeaves = useMemo(
    () =>
      scopedLeaveRequests.filter(
        (req) => (req.status || "").toLowerCase() === "approved"
      ),
    [scopedLeaveRequests]
  );

  /* ───────────────────────────── Metrics & Aggregations ───────────────────────────── */

  const statusCounts = useMemo(() => {
    return scopedLeaveRequests.reduce((acc, req) => {
      const key = (req.status || "Unknown").toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [scopedLeaveRequests]);

  const leaveTypes = useMemo(() => {
    return scopedLeaveRequests.reduce((acc, req) => {
      const key = req.leaveTypeLabel || req.leaveType || "Unknown";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [scopedLeaveRequests]);

  // ✅ Month labels based on range (cap to 12 months)
  const monthLabels = useMemo(() => {
    if (!rangeStart || !rangeEnd) {
      // fallback: last 12 months ending this month
      const end = DateTime.now()
        .setZone(companyTimeZone)
        .startOf("month");
      const start = end.minus({ months: 11 });
      const arr = [];
      for (let d = start; d <= end; d = d.plus({ months: 1 })) {
        arr.push(d.toFormat("yyyy-LL"));
      }
      return arr;
    }

    const start = DateTime.fromISO(rangeStart, {
      zone: companyTimeZone,
    }).startOf("month");
    const end = DateTime.fromISO(rangeEnd, { zone: companyTimeZone }).startOf(
      "month"
    );
    if (!start.isValid || !end.isValid) return [];

    const arr = [];
    for (let d = start; d <= end; d = d.plus({ months: 1 })) {
      arr.push(d.toFormat("yyyy-LL"));
      if (arr.length > 60) break;
    }
    if (arr.length > 12) return arr.slice(arr.length - 12);
    return arr;
  }, [rangeStart, rangeEnd, companyTimeZone]);

  const monthCounts = useMemo(() => {
    const byMonth = approvedLeaves.reduce((acc, req) => {
      const mk = DateTime.fromISO(req.startDate, {
        zone: companyTimeZone,
      }).toFormat("yyyy-LL");
      acc[mk] = (acc[mk] || 0) + 1;
      return acc;
    }, {});
    return monthLabels.map((m) => byMonth[m] || 0);
  }, [approvedLeaves, companyTimeZone, monthLabels]);

  // Hours used (approved)
  const totalApprovedHours = useMemo(() => {
    return approvedLeaves.reduce(
      (sum, req) => sum + calcRequestedHours(req),
      0
    );
  }, [approvedLeaves, calcRequestedHours]);

  // Company-wide only maps (still computed as hooks, but not rendered in drilldown)
  const leaveHoursPerEmployee = useMemo(() => {
    if (isDrilldown) return {};
    return approvedLeaves.reduce((acc, req) => {
      const hours = calcRequestedHours(req);
      if (!hours) return acc;

      const meta = employeesMap[req.employeeId] || {};
      const name = meta.name || req.employeeName || "Unknown";
      acc[name] = (acc[name] || 0) + hours;
      return acc;
    }, {});
  }, [isDrilldown, approvedLeaves, employeesMap, calcRequestedHours]);

  const leaveHoursPerDepartment = useMemo(() => {
    if (isDrilldown) return {};
    return approvedLeaves.reduce((acc, req) => {
      const hours = calcRequestedHours(req);
      if (!hours) return acc;

      const meta = employeesMap[req.employeeId] || {};
      const dept = meta.department || "Unknown / Unset";
      acc[dept] = (acc[dept] || 0) + hours;
      return acc;
    }, {});
  }, [isDrilldown, approvedLeaves, employeesMap, calcRequestedHours]);

  // KPIs
  const totalRequests = scopedLeaveRequests.length;
  const approvedCount = statusCounts["approved"] || 0;
  const pendingCount = statusCounts["pending"] || 0;

  const approvalRate = totalRequests
    ? ((approvedCount / totalRequests) * 100).toFixed(1)
    : "0.0";

  const avgHoursPerReq = approvedLeaves.length
    ? (totalApprovedHours / approvedLeaves.length).toFixed(1)
    : "0.0";

  const topType =
    Object.entries(leaveTypes)
      .sort((a, b) => b[1] - a[1])
      .map(([k]) => k)[0] || "—";

  /* ───────────────────────────── Chart Data & Options ───────────────────────────── */

  const pieOptions = useMemo(
    () => ({
      responsive: true,
      cutout: "56%",
      plugins: {
        legend: { position: "right" },
        tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw}` } },
      },
    }),
    []
  );

  const commonBarOpts = useMemo(
    () => ({
      responsive: true,
      plugins: { legend: { display: false } },
      scales: {
        x: { grid: { color: "rgba(0,0,0,0.08)" }, ticks: { precision: 0 } },
        y: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.06)" } },
      },
    }),
    []
  );

  const hoursBarOptions = useMemo(
    () => ({
      responsive: true,
      indexAxis: "y",
      plugins: { legend: { display: false } },
      scales: {
        x: { beginAtZero: true, grid: { color: "rgba(0,0,0,0.08)" } },
        y: { grid: { display: false } },
      },
    }),
    []
  );

  const statusData = useMemo(() => {
    const labels = ["approved", "pending", "rejected"].filter(
      (k) => statusCounts[k] !== undefined
    );
    return {
      labels,
      datasets: [
        {
          data: labels.map((k) => statusCounts[k]),
          backgroundColor: ["#22c55e", "#f59e0b", "#ef4444"],
          borderWidth: 0,
        },
      ],
    };
  }, [statusCounts]);

  const leaveTypeData = useMemo(
    () => ({
      labels: Object.keys(leaveTypes),
      datasets: [
        {
          data: Object.values(leaveTypes),
          backgroundColor: [
            "#6366f1",
            "#f87171",
            "#14b8a6",
            "#f59e0b",
            "#3b82f6",
            "#a78bfa",
          ],
          borderWidth: 0,
        },
      ],
    }),
    [leaveTypes]
  );

  const monthData = useMemo(
    () => ({
      labels: monthLabels.map((m) =>
        DateTime.fromFormat(m, "yyyy-LL").toFormat("LLL yyyy")
      ),
      datasets: [
        {
          label: "Approved Leave Requests",
          data: monthCounts,
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#36A2EB";
            const grad = g.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0
            );
            grad.addColorStop(0, "rgba(54, 162, 235, 0.95)");
            grad.addColorStop(1, "rgba(20, 184, 166, 0.55)");
            return grad;
          },
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 42,
        },
      ],
    }),
    [monthLabels, monthCounts]
  );

  // Company-wide chart data
  const hoursData = useMemo(() => {
    const topHours = Object.entries(leaveHoursPerEmployee)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
    return {
      labels: topHours.map(([name]) => name),
      datasets: [
        {
          label: "Hours Used",
          data: topHours.map(([, hrs]) => hrs),
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#06b6d4";
            const grad = g.createLinearGradient(
              0,
              chartArea.top,
              0,
              chartArea.bottom
            );
            grad.addColorStop(0, "rgba(6, 182, 212, 0.95)");
            grad.addColorStop(1, "rgba(99, 102, 241, 0.55)");
            return grad;
          },
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 42,
        },
      ],
    };
  }, [leaveHoursPerEmployee]);

  const deptData = useMemo(() => {
    const deptEntries = Object.entries(leaveHoursPerDepartment).sort(
      (a, b) => b[1] - a[1]
    );
    return {
      labels: deptEntries.map(([dept]) => dept),
      datasets: [
        {
          label: "Hours Used",
          data: deptEntries.map(([, hrs]) => hrs),
          backgroundColor: (ctx) => {
            const { chartArea, ctx: g } = ctx.chart;
            if (!chartArea) return "#f97316";
            const grad = g.createLinearGradient(
              chartArea.left,
              0,
              chartArea.right,
              0
            );
            grad.addColorStop(0, "rgba(244, 114, 182, 0.95)");
            grad.addColorStop(1, "rgba(251, 191, 36, 0.55)");
            return grad;
          },
          borderRadius: 8,
          borderSkipped: false,
          maxBarThickness: 42,
        },
      ],
    };
  }, [leaveHoursPerDepartment]);

  /* ───────────────────────────── UI ───────────────────────────── */
  if (!companyId) return <p>Loading...</p>;
  if (loading) return <p>Loading...</p>;

  return (
    <div className="management-analytics-wrapper">
      {error && <p style={{ color: "red" }}>{error}</p>}

      {/* ✅ Date range controls */}
      <div className="analytics-rangebar">
        <div className="analytics-filter">
          <label className="analytics-filter-label">Date Range</label>
          <select
            className="analytics-dropdown"
            value={rangePreset}
            onChange={(e) => setRangePreset(e.target.value)}
          >
            <option value="LAST_7">Last 7 days</option>
            <option value="LAST_30">Last 30 days</option>
            <option value="LAST_90">Last 90 days</option>
            <option value="MTD">Month to date</option>
            <option value="YTD">Year to date</option>
            <option value="CUSTOM">Custom</option>
          </select>
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">Start</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeStart}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeStart(e.target.value);
            }}
          />
        </div>

        <div className="analytics-filter">
          <label className="analytics-filter-label">End</label>
          <input
            className="analytics-date-input"
            type="date"
            value={rangeEnd}
            onChange={(e) => {
              setRangePreset("CUSTOM");
              setRangeEnd(e.target.value);
            }}
          />
        </div>

        <div className="analytics-range-pill">
          {rangeStart && rangeEnd
            ? `${formatMDY(rangeStart)} → ${formatMDY(rangeEnd)}`
            : "—"}
        </div>
      </div>

      {/* Hint */}
      {isDrilldown ? (
        <div className="analytics-hint">
          Showing Leave analytics for <b>1 employee</b>.
        </div>
      ) : (
        <div className="analytics-hint">
          Showing Leave analytics for <b>all employees</b>.
        </div>
      )}

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card accent-blue">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Total Requests</span>
          </div>
          <div className="kpi-value">{totalRequests}</div>
          <div className="kpi-sub">{pendingCount} pending</div>
        </div>

        <div className="kpi-card accent-green">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Approval Rate</span>
          </div>
          <div className="kpi-value">{approvalRate}%</div>
          <div className="kpi-sub">{approvedCount} approved</div>
        </div>

        <div className="kpi-card accent-teal">
          <div className="kpi-top">
            <span className="kpi-dot" />
            <span className="kpi-label">Avg Hours / Approved Request</span>
          </div>
          <div className="kpi-value">{avgHoursPerReq}</div>
          <div className="kpi-sub">Top type: {topType}</div>
        </div>
      </div>

      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Leave Status Breakdown</h3>
          <Pie data={statusData} options={pieOptions} />
        </div>

        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Most Common Leave Types</h3>
          <Pie data={leaveTypeData} options={pieOptions} />
        </div>

        <div className="chart-container" style={{ minHeight: 420 }}>
          <h3>Approved Leave Requests by Month</h3>
          <Bar data={monthData} options={commonBarOpts} />
        </div>

        {/* ✅ Company-wide ONLY */}
        {!isDrilldown && (
          <>
            <div className="chart-container" style={{ minHeight: 420 }}>
              <h3>Total Leave Hours per Employee (Top 10)</h3>
              <Bar data={hoursData} options={hoursBarOptions} />
            </div>

            <div className="chart-container" style={{ minHeight: 420 }}>
              <h3>Leave Hours by Department</h3>
              <Bar data={deptData} options={commonBarOpts} />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
